"use client"

import { useEffect, useState } from "react"
import { useEcomStore } from "@/context/ecom/EcomContext"
import { SSO_REDIRECT_FLAG } from "@/lib/hooks/useAuthCheck"
import { isKnownUser } from "@/lib/knownUserCookie"

const TIMEOUT_MS = 2000
const MAX_TIMEOUT_MS = 5000

export default function AuthGate({
  children,
}: {
  children: React.ReactNode
}) {
  const [state] = useEcomStore() ?? []
  const [timedOut, setTimedOut] = useState(false)
  // Only visitors with a prior TID session (see useAuthCheck) can trigger a
  // silent SSO redirect, so only they need content hidden until that
  // resolves. Read on mount (not during SSR) — the inline script in
  // src/app/[lang]/layout.tsx already hides content before paint for known
  // users via the `auth-check-pending` html class, so this only needs to be
  // correct by the time React takes over.
  const [needsGate, setNeedsGate] = useState(false)

  useEffect(() => {
    setNeedsGate(isKnownUser())
  }, [])

  useEffect(() => {
    // Only clear once resolved; useAuthCheck's loop-breaker needs the flag to survive the TID round trip.
    if (state?.isAuthCheckComplete) {
      sessionStorage.removeItem(SSO_REDIRECT_FLAG)
      return
    }
    if (!needsGate) return

    // Short timeout: reveal content unless a redirect is in progress
    const shortId = setTimeout(() => {
      if (!sessionStorage.getItem(SSO_REDIRECT_FLAG)) {
        setTimedOut(true)
      }
    }, TIMEOUT_MS)

    // Hard timeout: always reveal content (catch failed redirects)
    const hardId = setTimeout(() => {
      setTimedOut(true)
    }, MAX_TIMEOUT_MS)

    return () => {
      clearTimeout(shortId)
      clearTimeout(hardId)
    }
  }, [needsGate, state?.isAuthCheckComplete])

  // `.auth-gate` is visible by default (see globals.css) so visitors without
  // the `tid_known_user` cookie never wait on any JS. The layout's inline
  // blocking script adds `auth-check-pending` to <html> before first paint
  // for known users only, which is what actually hides this element — this
  // `auth-gate--visible` class is purely the "reveal once resolved" signal
  // for that subset, so it must start false here to match SSR exactly.
  const visible = state?.isAuthCheckComplete || timedOut

  return (
    <div className={`auth-gate ${visible ? "auth-gate--visible" : ""}`}>
      {children}
    </div>
  )
}

